import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, Clock, Heart, ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ImageManager } from '@/lib/imageManager';
import { isPackageBasedProduct, formatPackageDisplay, calculatePackageMetrics } from '@/lib/packageMeasurementUtils';
import { isOutOfStock } from '@/lib/inventoryUtils';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useCart } from '@/contexts/CartContext';
import { GroupBuyTooltipBadge } from '@/components/groupbuy/GroupBuyTooltipBadge';
import { LiquidationBadge } from '@/components/liquidation/LiquidationBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProductGradeBadge, { gradeConfig } from '@/components/ProductGradeBadge';

const placeholderImage = '/images/placeholder-construction-sketch.jpg';

interface Product {
  id: string;
  name: string;
  description: string;
  slug: string;
  price_per_unit: number;
  original_price?: number;
  measurement_unit: string;
  total_inventory: number;
  min_quantity: number;
  max_quantity?: number;
  step_quantity: number;
  city: string;
  sale_type: string;
  sale_start?: string;
  sale_end?: string;
  images: string[];
  main_image_index?: number;
  package_coverage_per_unit?: number;
  minimum_packages?: number;
  grade?: 'A' | 'B' | 'C' | 'D';
  created_at?: string;
  custom_tags?: string[];
  categories?: {
    name: string;
  };
  subcategories?: {
    name: string;
  };
  group_buy_enabled?: boolean;
  liquidation_sale_enabled?: boolean;
  liquidation_current_inventory?: number;
}

interface ProductCardProps {
  product: Product;
  viewMode?: 'grid' | 'list';
  showRegularSaleBadge?: boolean;
}

const ProductCard = ({ product, viewMode = 'grid', showRegularSaleBadge = false }: ProductCardProps) => {
  const navigate = useNavigate();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { items } = useCart();
  const [currentImageIndex, setCurrentImageIndex] = useState(product.main_image_index || 0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
  // Check if product is in cart
  const isInCart = items.some(item => item.id === product.id);

  // Check if product is new (created in last 30 days) and not overridden by custom tags
  const isNewProduct = () => {
    if (!product.created_at) return false;
    const createdDate = new Date(product.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    // Show "new" badge if product is recent AND admin hasn't removed it via custom_tags
    return createdDate > thirtyDaysAgo && (!product.custom_tags || !product.custom_tags.includes('hide_new'));
  };

  // Treat product as Group Buy if explicitly enabled OR if inventory is effectively infinite (999999)
  const isGroupBuy = product.group_buy_enabled || product.total_inventory >= 999999;

  const getTagLabel = (tag: string) => {
    const labels: Record<string, string> = {
      'new': 'חדש',
      'sale': 'במבצע',
      'featured': 'מומלץ',
      'limited': 'מוגבל'
    };
    return labels[tag] || tag;
  };

  const getTagVariant = (tag: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (tag === 'sale') return 'destructive';
    if (tag === 'featured') return 'default';
    if (tag === 'limited') return 'secondary';
    return 'outline';
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatMeasurementUnit = (unit: string) => {
    const units = {
      'square_meter': 'מ"ר',
      'meter': 'מטר',
      'unit': 'יח\'',
      'surface': 'משטח',
      'cubic_meter': 'מ"ק',
      'kilogram': 'ק"ג',
      'liter': 'ליטר',
      'package_by_meter': 'חבילה/יחידה',
      'package_by_square_meter': 'חבילה/יחידה'
    };
    return units[unit as keyof typeof units] || unit;
  };

  // Get display information for package-based products
  const getDisplayInfo = (product: Product) => {
    if (isPackageBasedProduct(product.measurement_unit)) {
      const baseUnit = product.measurement_unit === 'package_by_square_meter' ? 'square_meter' : 'meter';
      const coverage = product.package_coverage_per_unit || 1;
      const totalCoverage = (product.total_inventory * coverage).toLocaleString('he-IL', { maximumFractionDigits: 1 });

      return {
        isPackage: true,
        primaryPrice: product.price_per_unit,
        unitText: formatMeasurementUnit(baseUnit),
        detailsText: `ל${formatMeasurementUnit(baseUnit)} | חבילות של ${coverage} ${formatMeasurementUnit(baseUnit)}`,
        inventoryDisplay: `${totalCoverage} ${formatMeasurementUnit(baseUnit)}`
      };
    }

    return {
      isPackage: false,
      primaryPrice: product.price_per_unit,
      unitText: formatMeasurementUnit(product.measurement_unit),
      detailsText: `ל${formatMeasurementUnit(product.measurement_unit)} | מינימום ${product.min_quantity}`,
      inventoryDisplay: `${product.total_inventory} ${formatMeasurementUnit(product.measurement_unit)}`
    };
  };

  const isTimerSale = (product: Product) => {
    if (product.sale_type !== 'timer' || !product.sale_end) return false;
    const now = new Date();
    const endDate = new Date(product.sale_end);
    return now < endDate;
  };

  const calculateDiscountPercentage = (original: number, current: number) => {
    if (!original || original <= current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  const hasDiscount = (product: Product) => {
    return product.original_price && product.original_price > product.price_per_unit;
  };

  const getCurrentImageUrl = () => {
    if (!product.images || product.images.length === 0) return ImageManager.getImageUrl(undefined);
    return ImageManager.getImageUrl(product.images[currentImageIndex]);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.images || product.images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.images || product.images.length <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  const handleImageErrorWithFallback = (e: React.SyntheticEvent<HTMLImageElement>) => {
    ImageManager.handleImageError(e);
  };

  const handleClick = () => {
    // Use slug if available, otherwise use product ID as fallback
    const productIdentifier = product.slug || product.id;
    navigate(`/product/${productIdentifier}`);
  };

  if (viewMode === 'list') {
    return (
      <>
        <a
          href={`/product/${product.slug || product.id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              handleClick();
            }
          }}
          className="block"
        >
          <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardContent className="p-0">
              <div className="flex">
                {/* Image - Side */}
                <div className="relative w-48 h-32 min-h-[128px] max-h-[128px] flex-shrink-0">
                  <img
                    src={getCurrentImageUrl()}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={handleImageErrorWithFallback}
                  />
                  {/* Top Right Badges: Rating, Regular Sale, In Cart, Custom Tags */}
                  <div className="absolute top-2 right-2 flex flex-col gap-1 items-end z-10">
                    {/* Rating Badge */}
                    {product.grade && (
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsGradeModalOpen(true);
                        }}
                        className="cursor-pointer hover:scale-105 transition-transform shadow-md rounded-md bg-white/90 backdrop-blur-sm p-0.5"
                      >
                        <ProductGradeBadge grade={product.grade} size="sm" />
                      </div>
                    )}
                    {showRegularSaleBadge && (
                      <Badge className="bg-gradient-to-r from-blue-600 to-blue-400 text-white border-0 shadow-md text-xs">
                        מכירה רגילה
                      </Badge>
                    )}

                    {/* Show custom tags */}
                    {product.custom_tags?.map((tag) => (
                      <Badge key={tag} variant={getTagVariant(tag)} className="shadow-lg text-xs">
                        {getTagLabel(tag)}
                      </Badge>
                    ))}
                    {isTimerSale(product) && (
                      <Badge className="bg-red-500 text-white flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3" />
                        מכירה מוגבלת
                      </Badge>
                    )}
                  </div>

                  {/* Bottom Right Badges: Group Buy & Liquidation */}
                  <div className="absolute bottom-1 right-1 flex flex-col gap-1 items-end z-10">
                    {product.group_buy_enabled && (
                      <GroupBuyTooltipBadge size="sm" />
                    )}
                    {product.liquidation_sale_enabled && (
                      <LiquidationBadge />
                    )}
                  </div>

                  {/* Left Side: Favorite Button */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                    <Button
                      size="icon"
                      variant={isFavorite(product.id) ? "default" : "outline"}
                      className={`w-7 h-7 ${isFavorite(product.id) ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-background/90 hover:bg-red-50'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleFavorite(product.id);
                      }}
                    >
                      <Heart className={`w-3 h-3 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                    </Button>
                  </div>

                  {/* Bottom Left Badges: In Cart */}
                  <div className="absolute bottom-1 left-1 flex flex-col gap-1 z-10">
                    {isInCart && (
                      <Badge className="bg-lime/90 text-lime-foreground border-2 border-lime shadow-md text-xs px-2 py-0.5">
                        <ShoppingBag className="w-3 h-3 mr-1" />
                        בעגלה
                      </Badge>
                    )}
                  </div>

                  {/* Out of Stock Overlay */}
                  {isOutOfStock(product) && (
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-[1px] flex items-center justify-center z-20">
                      <div className="bg-gray-800/90 text-white text-xs font-bold px-3 py-1 rounded shadow-lg transform -rotate-6 border border-white/20">
                        אזל מהמלאי
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex flex-col gap-2">
                      <h3 className="text-lg font-semibold line-clamp-1 group-hover:text-lime transition-colors">
                        {product.name}
                      </h3>
                      <div className="flex flex-col gap-1">
                        {hasDiscount(product) && (
                          <span className="text-sm text-muted-foreground line-through">
                            {formatPrice(product.original_price!)}
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-lime">
                            {formatPrice(getDisplayInfo(product).primaryPrice)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {getDisplayInfo(product).detailsText}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Stock Badge Logic */}
                    {product.liquidation_sale_enabled && (
                      <Badge className="text-sm shadow-sm bg-red-600 hover:bg-red-700 text-white border-none">
                        במלאי: {getDisplayInfo(product).inventoryDisplay}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </a>
        <Dialog open={isGradeModalOpen} onOpenChange={setIsGradeModalOpen}>
          <DialogContent className="max-w-sm sm:max-w-md" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle className="text-right">דירוג המוצר: {product.grade}</DialogTitle>
            </DialogHeader>
            {product.grade && gradeConfig[product.grade] && (
              <div className="space-y-4 text-right" dir="rtl">
                <div className={`p-4 rounded-lg border ${gradeConfig[product.grade].bgColor} ${gradeConfig[product.grade].borderColor}`}>
                  <p className={`font-medium ${gradeConfig[product.grade].textColor}`}>
                    {gradeConfig[product.grade].description}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  דירוג זה נקבע על פי בדיקת איכות קפדנית של צוות Excess Market.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe) {
      if (isLeftSwipe) {
        // Next image
        if (!product.images || product.images.length <= 1) return;
        setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
      } else {
        // Prev image
        if (!product.images || product.images.length <= 1) return;
        setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
      }
    }
  };

  // Grid view (default)
  return (
    <>
      <a
        href={`/product/${product.slug || product.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            handleClick();
          }
        }}
        className="block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer">
          <div
            className="relative h-64 overflow-hidden touch-pan-y"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={getCurrentImageUrl()}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 select-none"
              onError={handleImageErrorWithFallback}
            />

            {/* Slider Controls */}
            {product.images && product.images.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className={`hidden md:block absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1 rounded-full backdrop-blur-sm transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={handleNextImage}
                  className={`hidden md:block absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-1 rounded-full backdrop-blur-sm transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                {/* Image Dots Indicator */}
                <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1.5 pointer-events-none">
                  {product.images.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-2 rounded-full transition-all duration-300 shadow-sm backdrop-blur-[1px] ${idx === currentImageIndex
                        ? "w-4 bg-black/70"
                        : "w-2 bg-black/20"
                        }`}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="absolute top-4 right-4 flex flex-col gap-2 z-10 items-end">
              {/* Rating Badge */}
              {product.grade && (
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsGradeModalOpen(true);
                  }}
                  className="cursor-pointer hover:scale-105 transition-transform shadow-md rounded-md bg-white/90 backdrop-blur-sm p-0.5"
                >
                  <ProductGradeBadge grade={product.grade} size="md" />
                </div>
              )}
            </div>

            <div className="absolute top-4 left-4 flex flex-col gap-2">


              <Button
                size="icon"
                variant={isFavorite(product.id) ? "default" : "outline"}
                className={`w-8 h-8 ${isFavorite(product.id) ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-background/90 hover:bg-red-50'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleFavorite(product.id);
                }}
              >
                <Heart className={`w-4 h-4 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
              </Button>
            </div>

            {/* Bottom Left Badges: In Cart */}
            <div className="absolute bottom-4 left-4 flex flex-col gap-2 z-10">
              {isInCart && (
                <Badge className="bg-lime/90 text-lime-foreground border-2 border-lime shadow-md">
                  <ShoppingBag className="w-3 h-3 mr-1" />
                  בעגלה
                </Badge>
              )}
            </div>

            <div className="absolute top-4 right-4 flex flex-col gap-2">
              {/* Tags moved to bottom-right as per request */}
              {/* Tags moved to bottom-right as per request */}
            </div>

            {isOutOfStock(product) && (
              <div className="absolute top-4 left-0 right-0 bg-gray-600/95 text-white text-center py-1.5 font-bold shadow-lg z-20 backdrop-blur-sm">
                אזל מהמלאי
              </div>
            )}


            <div className="absolute bottom-4 right-4 flex flex-col gap-2 items-end">
              {isGroupBuy && (
                <GroupBuyTooltipBadge />
              )}
              {product.liquidation_sale_enabled && (
                <LiquidationBadge
                  inventory={(() => {
                    if (isPackageBasedProduct(product.measurement_unit)) {
                      const coverage = product.package_coverage_per_unit || 1;
                      return parseFloat((product.total_inventory * coverage).toFixed(2));
                    }
                    return product.total_inventory;
                  })()}
                  unit={isPackageBasedProduct(product.measurement_unit) ? 'מ"ר' : formatMeasurementUnit(product.measurement_unit)}
                />
              )}
              {showRegularSaleBadge && (
                <Badge className="bg-gradient-to-r from-blue-600 to-blue-400 text-white border-0 shadow-md">
                  מכירה רגילה
                </Badge>
              )}
              {/* Show custom tags */}
              {product.custom_tags?.map((tag) => (
                <Badge key={tag} variant={getTagVariant(tag)} className="shadow-lg">
                  {getTagLabel(tag)}
                </Badge>
              ))}
              {isTimerSale(product) && (
                <Badge className="bg-red-500 text-white flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  מכירה מוגבלת
                </Badge>
              )}
            </div>
          </div>

          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-2 line-clamp-2 h-12 flex items-start group-hover:text-lime transition-colors">
              {product.name}
            </h3>




            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-col gap-1">
                {hasDiscount(product) && (
                  <span className="text-base text-muted-foreground line-through">
                    {formatPrice(product.original_price!)}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <div className="text-2xl font-bold text-lime">
                    {formatPrice(getDisplayInfo(product).primaryPrice)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {getDisplayInfo(product).detailsText}
                  </div>
                </div>
              </div>
            </div>


          </CardContent>
        </Card>
      </a>

      <Dialog open={isGradeModalOpen} onOpenChange={setIsGradeModalOpen}>
        <DialogContent className="max-w-sm sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-right">דירוג המוצר: {product.grade}</DialogTitle>
          </DialogHeader>
          {product.grade && gradeConfig[product.grade] && (
            <div className="space-y-4 text-right" dir="rtl">
              <div className={`p-4 rounded-lg border ${gradeConfig[product.grade].bgColor} ${gradeConfig[product.grade].borderColor}`}>
                <p className={`font-medium ${gradeConfig[product.grade].textColor}`}>
                  {gradeConfig[product.grade].description}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                דירוג זה נקבע על פי בדיקת איכות קפדנית של צוות Excess Market.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProductCard;